/**
 * Store Login rules: who can shop x how they pay x password x domains x
 * whether the store offers a shopper allowance.
 *
 * The editor's MIRROR of the server-owned table in
 * api/src/services/store_access_rules.py. The server is the control (its
 * update_campaign refuses a save that creates a dead end); this mirror lets
 * the editor keep an incomplete choice -- Email-domain restricted before a
 * domain is added, Shared password before one is typed -- as an unsaved
 * draft instead of autosaving a state the server would refuse.
 *
 * Tested row for row against storeAccessRules.matrix.json, which is the
 * server table's own output (regenerate: see that file's _generated_by).
 * Messages come from the same file, so the editor shows the server's words.
 *
 * Bug Intake #313, #343, #344, #346, #347 (Mike, stage deploy 19).
 */
import matrix from "./storeAccessRules.matrix.json";

export const ACCESS_MODES = ["open", "email_domain", "password", "invite_codes"];

export const PAYROLL_PAYMENT_METHOD = "Invoice / Payroll Deductions";

// Mirrors deferred_tender.PERMITTED_ACCESS_MODES (payroll).
const PAYROLL_PERMITTED_MODES = ["email_domain", "password"];

// Mirrors store_access_rules.ALLOWANCE_ACCESS_MODE (card #596, Jake
// 2026-10-29): an allowance belongs to a verified email address, so only an
// email-domain store may offer one.
const ALLOWANCE_ACCESS_MODE = "email_domain";

// Mirrors password_policy.MIN_STORE_PASSWORD_LENGTH and its refusal text. The
// server stays the control; the editor uses this to hold a half-typed
// password back instead of sending (and being refused on) every keystroke.
export const MIN_STORE_PASSWORD_LENGTH = 8;
export const PASSWORD_TOO_SHORT_MESSAGE = `Store passwords must be at least ${MIN_STORE_PASSWORD_LENGTH} characters`;

export const PROBLEMS = Object.freeze({
  PAYROLL_NEEDS_RESTRICTED_ACCESS: "payroll_needs_restricted_access",
  ALLOWANCE_NEEDS_RESTRICTED_ACCESS: "allowance_needs_restricted_access",
  PASSWORD_NOT_SET: "password_not_set",
  EMAIL_DOMAINS_EMPTY: "email_domains_empty",
  NET_CHECKOUT_NEEDS_CREDENTIAL: "net_checkout_needs_credential",
  NET_PASSWORD_CREDENTIAL_NEEDS_PASSWORD_MODE:
    "net_password_credential_needs_password_mode",
});

// Mirrors store_access_rules.NET_CREDENTIAL_MODE_*. Any other value is
// treated as PIN mode (it needs a PIN), the server's fail-closed reading.
export const NET_CREDENTIAL_MODE_PIN = "pin";
export const NET_CREDENTIAL_MODE_STORE_PASSWORD = "store_password";

/**
 * Problem codes for one configuration, in the server's order. Empty means
 * the configuration can be saved.
 */
export function accessConfigProblems(config) {
  // Keys are the server's column names (snake_case), read off the object
  // rather than destructured so the shape matches the wire one-for-one.
  const mode = config.access_mode;
  const problems = [];
  if (
    config.payment_method === PAYROLL_PAYMENT_METHOD &&
    !PAYROLL_PERMITTED_MODES.includes(mode)
  ) {
    problems.push(PROBLEMS.PAYROLL_NEEDS_RESTRICTED_ACCESS);
  }
  // Card #596: an allowance needs an email-domain store.
  if (config.allowance_active && !allowancePermitted(mode)) {
    problems.push(PROBLEMS.ALLOWANCE_NEEDS_RESTRICTED_ACCESS);
  }
  if (mode === "password" && !config.has_store_password) {
    problems.push(PROBLEMS.PASSWORD_NOT_SET);
  }
  const domains = config.email_domains;
  if (mode === "email_domain" && !(Array.isArray(domains) && domains.length)) {
    problems.push(PROBLEMS.EMAIL_DOMAINS_EMPTY);
  }
  if (config.net_checkout_enabled) {
    // P1-1: in store-password mode the credential IS the Shared password, so
    // the store must stay on Shared password; no PIN is needed.
    if (config.net_credential_mode === NET_CREDENTIAL_MODE_STORE_PASSWORD) {
      if (mode !== "password") {
        problems.push(PROBLEMS.NET_PASSWORD_CREDENTIAL_NEEDS_PASSWORD_MODE);
      }
    } else if (!config.has_net_credential) {
      problems.push(PROBLEMS.NET_CHECKOUT_NEEDS_CREDENTIAL);
    }
  }
  return problems;
}

export function problemMessage(code) {
  return matrix.messages[code] || "";
}

/**
 * May a store in this access mode offer a shopper allowance (card #596)?
 * Mirrors store_access_rules.allowance_permitted: email-domain only (Jake,
 * 2026-10-29); anything else (a shared password, open, invite codes,
 * unknown, unset) is refused.
 */
export function allowancePermitted(mode) {
  return mode === ALLOWANCE_ACCESS_MODE;
}

/** The allowance card's notice, in the server's words. */
export function allowanceRestrictionNotice() {
  return (
    (matrix.notices &&
      matrix.notices[PROBLEMS.ALLOWANCE_NEEDS_RESTRICTED_ACCESS]) ||
    ""
  );
}

/**
 * Is this server refusal one of the Store Login rules? The editor reverts
 * the Store Login slice only for these; any other refusal (a bad date, say)
 * is not about access, and reverting the owner's access choice for it would
 * throw away input that was never the problem. Matched on the server's own
 * message text, which comes from the same table as these rules.
 */
export function isAccessRuleRefusal(message) {
  if (typeof message !== "string") return false;
  if (Object.values(matrix.messages).includes(message)) return true;
  return Object.values(matrix.templates || {}).some((template) => {
    const [head, tail] = template.split("{domain}");
    return (
      tail !== undefined &&
      message.startsWith(head) &&
      message.endsWith(tail) &&
      message.length > head.length + tail.length
    );
  });
}

const LABEL = "[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?";
const DOMAIN_RE = new RegExp(`^(?:${LABEL}\\.)+${LABEL}$`);

/**
 * The canonical lowercase domain, or null when the value is not one. Same
 * rule as the server's normalize_email_domain: a single leading "@" is
 * dropped, anything else that is not a bare domain is refused.
 */
export function normalizeEmailDomain(value) {
  if (typeof value !== "string") return null;
  let candidate = value.trim().toLowerCase();
  if (candidate.startsWith("@")) candidate = candidate.slice(1);
  if (!candidate || candidate.length > 253) return null;
  return DOMAIN_RE.test(candidate) ? candidate : null;
}
